import { io, Socket } from 'socket.io-client';
import { getAccessToken, tryRefreshTokens, clearTokens } from './apiClient';

const WS_URL = process.env.REACT_APP_WS_URL || 'http://localhost:8000';

let socket: Socket | null = null;
const eventListeners: Map<string, Set<(data: any) => void>> = new Map();
const subscribedRobots: Set<string> = new Set();

export const initializeSocket = (): Socket => {
  // If already connected, return existing socket
  if (socket?.connected) {
    return socket;
  }

  // If socket exists but disconnected, disconnect fully and recreate
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(WS_URL, {
    // Provide fresh token on EVERY connection/reconnection attempt
    auth: (cb) => {
      cb({ token: getAccessToken() });
    },
    // Allow polling fallback — websocket-only fails silently behind strict
    // corporate proxies. Socket.IO upgrades to ws after the initial poll. (#43)
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  });

  socket.on('connect', () => {
    console.log('Socket connected, sid:', socket?.id);
    // Re-subscribe to all previously subscribed robots
    subscribedRobots.forEach((serial) => {
      socket?.emit('subscribe_robot', { robot_serial: serial });
      console.log('Re-subscribed to robot:', serial);
    });
    // v6.3.39: pose viewer sets are keyed by sid, so a reconnect needs re-arming
    try { rearmPoseStreams(); } catch { /* no-op */ }
  });
  
  // v6.3.32 single-session: server pushes this when the account logs in
  // elsewhere. Clear tokens and bounce to login.
  socket.on('force_logout', () => {
    try { clearTokens(); } catch {}
    window.location.href = '/login?reason=dual-login';
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
  });
  
  let refreshing = false;
  socket.on('connect_error', async (err) => {
    console.error('Socket connection error:', err.message);
    // v6.3.32: after the 30-min access token expires, every reconnect
    // attempt was rejected forever (reconnectionAttempts: Infinity spun on
    // a dead token). Refresh once per error, then let socket.io retry with
    // the fresh token supplied by the auth callback.
    if (!refreshing) {
      refreshing = true;
      try { await tryRefreshTokens(); } finally { refreshing = false; }
    }
  });

  // Forward all events to registered listeners dynamically (#21).
  // The old approach maintained a hardcoded whitelist of ~40 event names;
  // any new backend event would silently fail to reach listeners until the
  // whitelist was updated. onAny captures everything and dispatches based
  // on what's actually subscribed to via addEventListener().
  // Reserved/internal events (connect/disconnect/connect_error) are handled
  // by named handlers above and excluded here to avoid double-firing.
  const _reserved = new Set(['connect', 'disconnect', 'connect_error']);
  socket.onAny((event: string, ...args: unknown[]) => {
    if (_reserved.has(event)) return;
    const listeners = eventListeners.get(event);
    if (listeners) {
      const data = args.length === 1 ? args[0] : args;
      listeners.forEach((listener) => {
        try {
          listener(data);
        } catch (err) {
          console.error(`Listener for "${event}" threw:`, err);
        }
      });
    }
  });

  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  subscribedRobots.clear();
};

export const getSocket = () => socket;

export const isSocketConnected = () => socket?.connected || false;

export const addEventListener = (event: string, listener: (data: any) => void) => {
  if (!eventListeners.has(event)) eventListeners.set(event, new Set());
  eventListeners.get(event)?.add(listener);
};

export const removeEventListener = (event: string, listener: (data: any) => void) => {
  eventListeners.get(event)?.delete(listener);
};

export const subscribeToRobot = (robotSerial: string) => {
  // Already subscribed — skip to prevent spam
  if (subscribedRobots.has(robotSerial)) return;
  
  subscribedRobots.add(robotSerial);
  if (socket?.connected) {
    socket.emit('subscribe_robot', { robot_serial: robotSerial });
    console.log('Subscribed to robot:', robotSerial);
  }
};

export const unsubscribeFromRobot = (robotSerial: string) => {
  subscribedRobots.delete(robotSerial);
  socket?.emit('unsubscribe_robot', { robot_serial: robotSerial });
};

// ---- Live pose channel (v6.3.39) ----------------------------------------
// Viewer-gated: the robot only publishes pose while a map is actually open,
// so an idle fleet costs nothing. Re-armed automatically on reconnect.
const poseWatched = new Set<string>();

export const startPoseStream = (robotSerial: string) => {
  poseWatched.add(robotSerial);
  socket?.emit('pose_view_start', { robot_serial: robotSerial });
};

export const stopPoseStream = (robotSerial: string) => {
  poseWatched.delete(robotSerial);
  socket?.emit('pose_view_stop', { robot_serial: robotSerial });
};

/** Re-subscribe after a reconnect — server-side viewer sets are per-sid. */
export const rearmPoseStreams = () => {
  poseWatched.forEach((serial) => socket?.emit('pose_view_start', { robot_serial: serial }));
};

export const sendTeleop = (robotSerial: string, linear: number, angular: number) => {
  socket?.emit('teleop', { robot_serial: robotSerial, linear, angular });
};

export const sendNavGoal = (robotSerial: string, x: number, y: number, theta: number) => {
  console.log('sendNavGoal:', { robotSerial, x, y, theta });
  socket?.emit('nav_goal', { robot_serial: robotSerial, x, y, theta });
};

export const cancelNavigation = (robotSerial: string) => {
  socket?.emit('cancel_nav', { robot_serial: robotSerial });
};

export const emergencyStop = (robotSerial: string) => {
  socket?.emit('emergency_stop', { robot_serial: robotSerial });
};

export const startSlam = (robotSerial: string) => {
  socket?.emit('slam_start', { robot_serial: robotSerial });
};

export const stopSlam = (robotSerial: string) => {
  socket?.emit('slam_stop', { robot_serial: robotSerial });
};

export const saveSlamMap = (robotSerial: string, name: string) => {
  socket?.emit('slam_save', { robot_serial: robotSerial, name });
};

export const loadMap = (robotSerial: string, mapId: string, waypoints: any[] = []) => {
  socket?.emit('load_map', { robot_serial: robotSerial, map_id: mapId, waypoints });
};

export const sendConfig = (robotSerial: string, config: any) => {
  socket?.emit('config_update', { robot_serial: robotSerial, config });
};

export const getConfig = (robotSerial: string) => {
  console.log('getConfig called for:', robotSerial, 'socket connected:', socket?.connected);
  if (socket?.connected) {
    socket.emit('get_config', { robot_serial: robotSerial });
  } else {
    console.warn('getConfig: socket not connected, cannot request config');
  }
};

export const emitEvent = (event: string, data: any) => {
  socket?.emit(event, data);
};

export const shutdownRobot = (robotSerial: string) => {
  socket?.emit('shutdown', { robot_serial: robotSerial });
};

export const rebootRobot = (robotSerial: string) => {
  socket?.emit('reboot', { robot_serial: robotSerial });
};

// ========== Map Manager Functions ==========

export const requestRobotsOnline = () => {
  socket?.emit('get_robots_online', {});
};

export const requestLicenseStatus = () => {
  socket?.emit('get_license_status', {});
};

export const confirmAtCharger = (robotSerial: string) => {
  socket?.emit('confirm_at_charger', { robot_serial: robotSerial });
};

export const navToWaypoint = (robotSerial: string, waypointName: string) => {
  socket?.emit('nav_to_waypoint', { robot_serial: robotSerial, waypoint_name: waypointName });
};

export const getMapManagerStatus = (robotSerial: string) => {
  socket?.emit('get_map_manager_status', { robot_serial: robotSerial });
};

// ========== SLAM View Tracking ==========

/**
 * Notify backend that user started viewing SLAM page.
 * Used for resource management and tracking active SLAM viewers.
 */
export const slamViewStart = (robotSerial: string) => {
  socket?.emit('slam_view_start', { robot_serial: robotSerial });
};

/**
 * Notify backend that user stopped viewing SLAM page.
 * Allows backend to optimize resources when no one is watching.
 */
export const slamViewStop = (robotSerial: string) => {
  socket?.emit('slam_view_stop', { robot_serial: robotSerial });
};

// ========== User Session Notifications (Privacy Mode) ==========

/**
 * Notify backend that user has logged in and which robots they own.
 * This triggers ACTIVE mode on the robots' cloud bridge nodes.
 */
export const notifyUserLoggedIn = (userId: string, robotSerials: string[]) => {
  if (socket?.connected) {
    socket.emit('user_logged_in', { user_id: userId, robot_serials: robotSerials });
    console.log('Notified backend: user_logged_in for robots:', robotSerials);
  }
};

/**
 * Notify backend that user has logged out.
 * This triggers RESTRICTED mode on the robots' cloud bridge nodes.
 */
export const notifyUserLoggedOut = (userId: string) => {
  if (socket?.connected) {
    socket.emit('user_logged_out', { user_id: userId });
    console.log('Notified backend: user_logged_out');
  }
};

// ========== Map Upload ==========

/**
 * Request map upload from robot to cloud.
 * Robot will prepare and send map files via upload_map event.
 */
export const requestMapUpload = (robotSerial: string, mapName: string, mapId?: string) => {
  socket?.emit('upload_map_request', { 
    robot_serial: robotSerial, 
    map_name: mapName,
    map_id: mapId || ''
  });
};
